import { useEffect, useState } from "react";
import { api, formatDateTime, SECTION_LABELS } from "@/lib/api";
import { CalendarDays, MapPin, Users2, PartyPopper, Landmark, CalendarPlus, Download } from "lucide-react";

function Card({ m, onClick, accent }) {
  const backend = process.env.REACT_APP_BACKEND_URL;
  const gcal = (() => {
    const fmt = (d) => new Date(d).toISOString().replace(/[-:]/g, "").split(".")[0] + "Z";
    // Slash tra le date letterale (URLSearchParams lo codifica in %2F e Google non legge le date)
    return "https://calendar.google.com/calendar/render?action=TEMPLATE"
      + `&text=${encodeURIComponent(m.title || "")}`
      + `&dates=${fmt(m.start)}/${fmt(m.end)}`
      + `&details=${encodeURIComponent(m.description || "")}`
      + `&location=${encodeURIComponent(m.location || "")}`;
  })();
  const stop = (e) => e.stopPropagation();
  return (
    <div onClick={onClick} data-testid={`upcoming-${m.id}`}
      className="text-left rounded-xl p-3 bg-white border border-[#E8E2D8] hover:shadow-md transition min-w-0 cursor-pointer">
      <div className="flex items-baseline gap-2">
        <div className="font-display text-2xl font-bold leading-none" style={{ color: accent }}>{new Date(m.start).getDate()}</div>
        <div className="text-xs uppercase tracking-widest text-[#8A8272] font-bold">{new Date(m.start).toLocaleDateString("it-IT", { month: "short" })}</div>
        <span className={`role-${m.section} ml-auto text-[9px] px-1.5 py-0.5 rounded-full font-semibold uppercase`}>{SECTION_LABELS[m.section] || m.section}</span>
      </div>
      <div className="font-display text-sm font-bold text-[#2B3A33] mt-2 line-clamp-2">{m.title}</div>
      <div className="text-[11px] text-[#6B6558] mt-1">{formatDateTime(m.start)}</div>
      {m.location && <div className="text-[11px] text-[#8A8272] mt-1 flex items-center gap-1 truncate"><MapPin className="w-3 h-3 shrink-0" />{m.location}</div>}
      {m.rsvp_yes_count > 0 && (
        <div className="text-[11px] text-[#16A34A] font-semibold mt-1 flex items-center gap-1">
          <Users2 className="w-3 h-3" /> {m.rsvp_yes_count} {m.rsvp_yes_count === 1 ? "socio confermato" : "soci confermati"}
        </div>
      )}
      <div className="mt-2 flex gap-1.5" onClick={stop}>
        <a href={gcal} target="_blank" rel="noreferrer" data-testid={`upcoming-gcal-${m.id}`}
          className="text-[10px] px-2 py-1 rounded-lg bg-[#1A73E8] text-white font-semibold inline-flex items-center gap-1 hover:bg-[#1558B0]">
          <CalendarPlus className="w-3 h-3" /> Google
        </a>
        <a href={`${backend}/api/meetings/${m.id}/ics`} data-testid={`upcoming-ics-${m.id}`}
          className="text-[10px] px-2 py-1 rounded-lg border border-[#E8E2D8] bg-white font-semibold inline-flex items-center gap-1 hover:bg-[#F3EEE4]">
          <Download className="w-3 h-3" /> iPhone
        </a>
      </div>
    </div>
  );
}

function Group({ title, icon: Icon, items, accent, bg, onOpenCalendar, testId }) {
  if (!items.length) return null;
  return (
    <div data-testid={testId} className={`paper rounded-2xl p-5 border border-[#E8E2D8] ${bg}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="text-xs uppercase tracking-widest font-bold flex items-center gap-2" style={{ color: accent }}>
          <Icon className="w-3.5 h-3.5" /> {title}
        </div>
        {onOpenCalendar && <button onClick={onOpenCalendar} className="text-xs font-semibold text-[#2B3A33] hover:underline" data-testid={`${testId}-more`}>Vedi tutte →</button>}
      </div>
      <div className="grid gap-2 sm:grid-cols-3 [&>*]:min-w-0">
        {items.map(m => <Card key={m.id} m={m} onClick={onOpenCalendar} accent={accent} />)}
      </div>
    </div>
  );
}

export default function UpcomingWidget({ onOpenCalendar }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get("/meetings/upcoming?limit=6").then(r => setItems(r.data)).catch(() => setItems([])).finally(() => setLoading(false));
  }, []);

  if (loading || !items.length) return null;
  const events = items.filter(m => m.section === "tutti").slice(0, 3);
  const meetings = items.filter(m => m.section !== "tutti").slice(0, 3);

  return (
    <div data-testid="upcoming-widget" className="grid gap-4 mb-6">
      <Group title="Prossimi Eventi" icon={PartyPopper} items={events} accent="#EF3E36" bg="bg-gradient-to-br from-[#FDF2F2] via-white to-[#F3EEE4]" onOpenCalendar={onOpenCalendar} testId="upcoming-events" />
      <Group title="Prossime Riunioni" icon={Landmark} items={meetings} accent="#2B3A33" bg="bg-gradient-to-br from-[#E8EFEB] via-white to-[#F3EEE4]" onOpenCalendar={onOpenCalendar} testId="upcoming-meetings" />
    </div>
  );
}
